type QueryPayload = {
    success?: boolean
    rows?: unknown[]
    result?: { results?: unknown[] }[]
    errors?: { message?: string }[]
}

/**
 * Thin server-only client for the project database.
 * The endpoint speaks HTTP, not the Postgres wire protocol, and answers with
 * the Cloudflare shape, repeating the last statement's rows at the top level.
 */
export const query = async <T>(sql: string, params: unknown[] = []): Promise<T[]> => {
    if (typeof window !== 'undefined') {
        throw new Error('The database client is server-only.')
    }

    const url = process.env.DATABASE_URL
    const token = process.env.DATABASE_TOKEN

    if (!url || !token) {
        throw new Error('The database connection is not configured.')
    }

    const response = await fetch(url, {
        method: 'POST',
        headers: {
            'Content-Type': 'application/json',
            Authorization: `Bearer ${token}`
        },
        body: JSON.stringify({ sql, params })
    })

    if (!response.ok) {
        throw new Error(`Database request failed with status ${response.status}`)
    }

    const payload: QueryPayload = await response.json()

    if (payload.success === false) {
        throw new Error(payload.errors?.[0]?.message ?? 'The database rejected the statement.')
    }

    const rows = payload.rows ?? payload.result?.[0]?.results ?? []

    return rows as T[]
}

export const queryOne = async <T>(sql: string, params: unknown[] = []): Promise<T | null> => {
    const rows = await query<T>(sql, params)
    return rows[0] ?? null
}
